import React from 'react';
import { formatDate, formatDecimal, formatMoney } from './contract';
import { linkTypeLabel } from './Candidates/contract';

/** Colunas que se repetem nas abas de lista do Kanban (5667–5671). */
const missing = 'Não informado';

/** Janela agendada: início e máxima ("17/10/2026 08:00 – 17/10/2026 16:00"); sem início, só a máxima. */
export function windowColumn(key, label, startOf, endOf, width = 260) {
  return {
    key,
    label,
    width,
    value: (row) => {
      const start = startOf(row);
      const end = endOf(row);
      if (!start && !end) return missing;
      return start && end && start !== end
        ? `${formatDate(start)} – ${formatDate(end)}`
        : formatDate(end || start);
    },
  };
}

/** Distância em metros do backend, mostrada em km com até duas casas. */
export const kmLabel = (meters) =>
  meters == null ? missing : `${formatDecimal(Number((meters / 1000).toFixed(2)))} km`;

export function kmColumn(key, label, metersOf, width = 120) {
  return { key, label, width, value: (row) => kmLabel(metersOf(row)) };
}

/** Atualizado, com o informativo pedido no cabeçalho. */
export const updatedColumn = {
  key: 'updated',
  label: 'Atualizado',
  width: 170,
  hint: 'Data e a hora da última alteração de qualquer informação da viagem',
  value: (row) => formatDate(row.updatedAt),
};

/** PCP: usuário que criou a carga, com a data e a hora da criação. */
export const pcpColumn = {
  key: 'pcp',
  label: 'PCP',
  width: 220,
  value: (row) =>
    [row.createdBy?.name, row.loadCreatedAt && formatDate(row.loadCreatedAt)]
      .filter(Boolean)
      .join(' · ') || missing,
};

/** Valor a Receber: valor do tomador em reais; em outra moeda, o valor original com aviso. */
export const amountColumn = {
  key: 'amount',
  label: 'Valor a Receber',
  width: 125,
  className: 'tc-k-cell-amount',
  value: (row) => {
    const quoted = row.amount === null && row.sourceAmount != null;
    const value = formatMoney(
      quoted ? row.sourceAmount : row.amount,
      quoted ? row.sourceCurrency || null : 'BRL',
    );
    return quoted ? `${value} · Valor original; total em reais não informado.` : value;
  },
};

/** Valor a Pagar: frete negociado da viagem (5676), na moeda da carga. */
export const payableColumn = {
  key: 'payable',
  label: 'Valor a Pagar',
  width: 125,
  value: (row) => formatMoney(row.payableAmount, row.sourceCurrency || 'BRL'),
};

/** Vínculo Placa: mesma classificação do modal Veículos disponíveis (5676). */
export const linkColumn = {
  key: 'link',
  label: 'Vínculo Placa',
  width: 190,
  value: (row) => linkTypeLabel(row.linkType),
};

/** Negociador: usuário, data e hora do atendimento (5676). */
export const negotiatorColumn = {
  key: 'negotiator',
  label: 'Negociador',
  width: 220,
  value: (row) =>
    [row.attendedBy?.name, row.attendedAt && formatDate(row.attendedAt)]
      .filter(Boolean)
      .join(' · ') || missing,
};

/**
 * Prazo pelo documento 1.6 (26/10/2026): até a Data máxima, inclusive antes da Data início, a
 * viagem está no prazo; depois dela, fora do prazo.
 */
export function deadline(at, max) {
  if (!max || !at) return missing;
  return Date.parse(at) > Date.parse(max) ? 'Fora do prazo' : 'No prazo';
}

// Com Chegada e Efetivado preenchidos, compara as duas datas; senão, o horário atual.
function deadlineOf(point, max, now) {
  const dates =
    point?.arrivedAt && point?.completedAt ? [point.arrivedAt, point.completedAt] : [now];
  return dates.some((at) => deadline(at, max) === 'Fora do prazo')
    ? 'Fora do prazo'
    : deadline(dates[0], max);
}

/**
 * Prazo da Lista de viagens por status (documento 1.6, 5671): janela da origem até Carregamento
 * finalizado; janela do destino a partir de Aguardando emissão. Aguardando descarregamento segue
 * Em análise (resposta do negócio, 26/10/2026).
 */
export function travelDeadline(row, now = new Date().toISOString()) {
  const pickup = row.scheduledPickupAt;
  const delivery = row.scheduledDeliveryAt;
  switch (row.status?.code) {
    case 'ST2':
    case 'RELEASED_COLLECTION':
      return deadlineOf(row.origin, pickup, now);
    case 'ST8':
      return deadline(row.origin?.arrivedAt || now, pickup);
    case 'ENDED_LOADING':
      return deadline(row.origin?.completedAt || now, pickup);
    case 'ST4':
    case 'ST5':
      return deadlineOf(row.destination, delivery, now);
    default:
      return deadline(now, delivery);
  }
}

export function deadlineColumn(labelOf) {
  return {
    key: 'deadline',
    label: 'Prazo',
    width: 125,
    value: labelOf,
    render: (_, value) => (
      <span
        className={
          value === 'Fora do prazo' ? 'tc-k-late' : value === 'No prazo' ? 'tc-k-ontime' : undefined
        }
      >
        {value}
      </span>
    ),
  };
}

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
/** "6 horas e 30 minutos": dias, horas e minutos desde a entrada em Em viagem. */
export function elapsedLabel(since, now = Date.now()) {
  const start = Date.parse(since);
  if (!Number.isFinite(start) || start > now) return missing;
  const minutes = Math.floor((now - start) / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor(minutes / 60) % 24;
  const rest = minutes % 60;
  const parts = [
    days && plural(days, 'dia', 'dias'),
    hours && plural(hours, 'hora', 'horas'),
    (rest || (!days && !hours)) && plural(rest, 'minuto', 'minutos'),
  ].filter(Boolean);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} e ${parts.at(-1)}` : parts[0];
}

/** Tempo em trânsito: agora menos a entrada em Em viagem pelo Feed (5678). */
export const transitTimeColumn = {
  key: 'transitTime',
  label: 'Tempo Em Trânsito',
  width: 200,
  value: (row) => elapsedLabel(row.inTransitStartedAt),
};
